import ab, { ABTestID, DynamicABTestID } from 'src/replicant/ruleset/ab';
import StateObserver, { user } from 'src/StateObserver';
import { AB } from './AB';
import { getDynamicTestBucket } from '../replicant/getters/ab';
import { GCInstant, GCInstant as platform } from 'src/lib/gcinstant';
import { inferChatbotGroup } from './chatbot/chatbot.utils';
import { getChatbotGroup } from 'src/replicant/chatbot/chatbot.getters';
import { kickCurrentVisitors } from 'src/game/visit/visit.login';
import { updateInactiveFriendsProperty } from 'src/game/gifts/gifts.sequence';
import { isPlatformNative } from './native/native.util';
import { isPlatformGuest, isQuotaExceededError } from './utils';
import { captureGenericError } from './sentry';
import { isFirstSession } from 'src/replicant/utils/platform';
import { DemoState, DemoTestBucket } from './getDemoState';
import { trackDemoSuccessMigration } from 'src/game/demo/demo.analytics';
import { setUserProperties } from './analytics/utils';
import PNsManager from 'src/game/native/push.notifications';
import { isInLineMiniApp } from '@play-co/gcinstant';

// Executed only once after user is logged in per session. Does not need to be
// repeated when user state is refreshed. Cannot be part of the replicant
// onLogin action (if we ever do it), since it depends on platform.
export async function sessionSetup() {
  const { user } = StateObserver.getState();

  // Set up A/B tests
  StateObserver.invoke.setAbTests(
    (Object.keys(ab.config) as ABTestID[]).reduce((result, testId) => {
      const bucket = AB.getBucketID(testId);
      if (!bucket) {
        return result;
      }

      return { ...result, [testId]: bucket };
    }, {}),
  );

  // Migrate user state from demo mode users
  if (isFirstSession(user) && !process.env.REPLICANT_OFFLINE) {
    const url = new URL(window.location.href);
    const demoState = url.searchParams.get('demoPayload');

    if (demoState) {
      try {
        const parsedDemoState = JSON.parse(demoState) as DemoState;
        StateObserver.invoke.resumeGame({
          demoBucketId: parsedDemoState.bucketId_331 ?? '',
          petName: parsedDemoState.petName,
          evolution: parsedDemoState.petEvolutions ?? [],
          tos: parsedDemoState.tos,
        });

        trackDemoSuccessMigration(
          parsedDemoState.bucketId_331 as DemoTestBucket,
        );
      } catch (e) {
        console.error('Error in demo mode game migration:', e);
        captureGenericError('Error in demo mode game migration', e);
      }
    }
  }

  // Remove user properties for any tests from which the user was unassigned
  (Object.keys(ab.dynamicAB) as DynamicABTestID[]).forEach((test) => {
    const { user } = StateObserver.getState();
    if (!getDynamicTestBucket(user, test)) {
      setUserProperties({ [`abTest_${test}`]: '(none)' });
    }
  });

  kickCurrentVisitors();

  // Detect PNs status, for native app
  if (PNsManager.isApiAvailable()) {
    const shouldRegister = await PNsManager.shouldRegister();
    if (shouldRegister) {
      // Registration will also update status
      await PNsManager.register();
    } else {
      await PNsManager.updateStatus();
    }
  }
}

// Called just before EntryFinal is sent
export async function sessionSetupBeforeEntryFinal() {
  // Find out if is an adult user, by first checking if user is in adult chatbot group,
  // then checking if user is marked as adult in demographics.
  const group = getChatbotGroup(user());
  const isAdult =
    group === 'newAdult' ||
    group === 'oldAdult' ||
    !!GCInstant.getDemographics()?.isAdult;

  // Note: isSubscribedToOA is passed here (not in onLogin) because GCInstant.isSubscribedToBot
  // is not resolved at onLogin time. See: https://github.com/play-co/buruburu/issues/10620
  await StateObserver.invoke.onBeforeEntryFinal({
    isAdult,
    isInLineMiniApp: isInLineMiniApp(),
    isSubscribedToOA: GCInstant.isSubscribedToBot,
  });
}

// Called after EntryFinal is sent
export async function sessionSetupAfterEntryFinal() {
  // Update chatbot group.
  if (!process.env.IS_DEVELOPMENT) {
    await StateObserver.invoke.updateChatbotGroup(inferChatbotGroup());
  }

  updateInactiveFriendsProperty();

  // If user came from guest login we add special flag tro local storage to identify this user after LINE login
  // https://github.com/play-co/buruburu/issues/5935
  try {
    if (isPlatformGuest(platform)) {
      localStorage.setItem('hasPlayedAsGuest', 'true');
    }
  } catch (e) {
    console.error('Error setting has played as guest flag', e);
    captureGenericError('Error setting has played as guest flag', null);
  }

  // https://github.com/play-co/buruburu/issues/6015
  // Set persistent flag that will track if users re-installs is tracked and local storage flag to detect re-install.
  // If user re-install app it will clear local storage, and we can detect possible re-install by checking this flag and local storage flag.
  // @see User property `lastEntryIsFirstOfNativeInstall`
  if (isPlatformNative()) {
    const { isFirstOfNativeInstallTracked } = user().native;

    try {
      // Set and restore lastEntryIsFirstOfNativeInstall local storage flag
      if (!isFirstOfNativeInstallTracked) {
        localStorage.setItem('lastEntryIsFirstOfNativeInstall', 'true');
        await StateObserver.invoke.setFirstOfNativeInstallTrackedFlag(true);
      } else if (
        isFirstOfNativeInstallTracked &&
        !localStorage.getItem('lastEntryIsFirstOfNativeInstall')
      ) {
        // Restore flag if it was cleared, it should be already sent on entry finale event and last properties.
        localStorage.setItem('lastEntryIsFirstOfNativeInstall', 'true');
      }
    } catch (e) {
      if (isQuotaExceededError(e)) {
        console.warn(
          'Local storage quota exceeded, cannot set lastEntryIsFirstOfNativeInstall flag',
        );
        // Do not report error if local storage is full, since it is not critical.
        return;
      }

      console.error('Error setting first of native install tracked flag', e);
      captureGenericError(
        'Error setting first of native install tracked flag',
        null,
      );
    }
  }
}
